import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

interface AppwriteCacheDB extends DBSchema {
  'appwrite-cache': {
    key: string;
    value: {
      data: any;
      /** Momento en el que se guardó el dato */
      timestamp: number;
      /** Momento recomendado de expiración (solo se respeta cuando hay red) */
      expiresAt: number;
    };
  };
}

class AppwriteCacheService {
  private dbName = 'worksphere-appwrite-cache';
  private version = 1;
  private db: IDBPDatabase<AppwriteCacheDB> | null = null;

  // duracion del cache
  private DEFAULT_CACHE_DURATION = 7 * 24 * 60 * 60 * 1000;// 7 dias minutos

  async init() {
    if (this.db) return this.db;

    this.db = await openDB<AppwriteCacheDB>(this.dbName, this.version, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('appwrite-cache')) {
          db.createObjectStore('appwrite-cache');
        }
      },
    });

    return this.db;
  }

  // Generar clave única para cada query
  private generateKey(type: string, params: any): string {
    return `${type}:${JSON.stringify(params)}`;
  }

  /**
   * Obtiene un valor de caché.
   * - Si `allowStaleOffline` es true, y estamos offline, se devolverá el dato
   *   aunque haya pasado su `expiresAt`. Esto mantiene visible el contenido
   *   previamente cargado en recargas offline.
   */
  async get(
    type: string,
    params: any,
    options?: { allowStaleOffline?: boolean }
  ): Promise<any | null> {
    const db = await this.init();
    const key = this.generateKey(type, params);
    const cached = await db.get('appwrite-cache', key);

    if (!cached) return null;

    const now = Date.now();
    const isExpired = now > cached.expiresAt;

    // Si estamos offline y se permite usar datos "stale", los devolvemos
    if (isExpired && options?.allowStaleOffline && !navigator.onLine) {
      console.warn(`Cache STALE pero permitido offline: ${type}`);
      return cached.data;
    }

    // Si expiró y no estamos en modo "stale offline", limpiar y no devolver nada
    if (isExpired) {
      await db.delete('appwrite-cache', key);
      return null;
    }

    console.log(` Cache HIT: ${type}`);
    return cached.data;
  }

  async set(type: string, params: any, data: any, duration?: number) {
    const db = await this.init();
    const key = this.generateKey(type, params);
    const cacheDuration = duration || this.DEFAULT_CACHE_DURATION;

    await db.put('appwrite-cache', {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + cacheDuration,
    }, key);

    console.log(` Cache SET: ${type}`);
  }

  async clear() {
    const db = await this.init();
    await db.clear('appwrite-cache');
  }

  // Wrapper para llamadas a Appwrite
  async wrapCall<T>(
    type: string,
    params: any,
    networkCall: () => Promise<T>,
    cacheDuration?: number
  ): Promise<T> {
    try {
      // Si está offline, intentar caché directo
      if (!navigator.onLine) {
        const cached = await this.get(type, params, { allowStaleOffline: true });
        if (cached) {
          return cached as T;
        }
        throw new Error(`Sin conexion y no hay datos en cache para ${type}`);
      }
      // Si está online, intenta red primero
      try {
        const result = await networkCall();
        // Guardar en caché
        await this.set(type, params, result, cacheDuration);
        return result;
      } catch (networkError: any) {
        // Si falla la red, intentar cache
        if (networkError.message?.includes('fetch') ||
          networkError.message?.includes('network') ||
          networkError.message?.includes('INTERNET_DISCONNECTED')) {
          const cached = await this.get(type, params);
          if (cached) {
            console.warn(`Red falló, usando caché para ${type}`);
            return cached as T;
          }
        }
        throw networkError;
      }

    } catch (error) {
      console.error(`Error en wrapCall para ${type}:`, error);
      throw error;
    }
  }
}

export const appwriteCache = new AppwriteCacheService();